import { useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Col, Collapse, ColorPicker, Form, Input, Modal, Row, Switch, Tooltip } from 'antd';
import { DownloadOutlined, RightOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import MatrixList from './MatrixList';
import RiskMatrix from './RiskMatrix';
import { NotificationContext } from '../../../providers/NotificationProvider';
import { getProject } from '../../../features/projects/projectSlice';
import {
  createLikelihoodLevel,
  createMatrix,
  createRiskLevel,
  createSeverityLevel,
  deleteLikelihoodLevel,
  deleteSeverityLevel,
  getMatrix,
  updateLikelihoodLevel,
  updateMatrix,
  updateRiskLevel,
  updateSeverityLevel,
  updateMatrixInverse,
} from '../../../features/projects/matrixSlices';
import {
  MatrixLikelihoodType,
  MatrixRiskType,
  MatrixSeverityType,
  MatrixType,
  ProjectType,
} from '../../../types/types';
import { exportRiskMatrixToXlsx } from './exportMatrix';

const DEFAULT_CELL_COLOR = 'FFFFFF';
const DEFAULT_FONT_COLOR = '000000';
const MAX_COLOR_HISTORY = 20;

const getInitialColorHistory = (): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const stored = window.localStorage.getItem('matrixColorHistory');
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeHexColor = (value?: string | null): string => {
  if (!value) return '';
  const cleaned = value.replace(/#/g, '').trim().toUpperCase();
  if (/^[0-9A-F]{6}$/.test(cleaned)) {
    return cleaned;
  }
  if (/^[0-9A-F]{3}$/.test(cleaned)) {
    return cleaned
      .split('')
      .map((char) => `${char}${char}`)
      .join('');
  }
  return '';
};

type ColorPickerValue = {
  toHex?: () => string;
  toHexString?: () => string;
} | null;

const extractHexFromPicker = (value?: ColorPickerValue | string | null, css?: string): string => {
  const fromValueString = typeof value === 'string' ? normalizeHexColor(value) : '';
  if (fromValueString) return fromValueString;

  if (value && typeof value === 'object') {
    const fromHexString = normalizeHexColor(value.toHexString?.());
    if (fromHexString) return fromHexString;
    const fromHex = normalizeHexColor(value.toHex?.());
    if (fromHex) return fromHex;
  }

  return normalizeHexColor(css);
};

type SelectedCell = {
  severity: MatrixSeverityType;
  likelihood: MatrixLikelihoodType;
  risk?: MatrixRiskType;
};

const parseOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const resolveMatrixIdFromContext = (matrixIdParam?: string | undefined, ...candidates: Array<number | null | undefined>) => {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && !Number.isNaN(candidate)) {
      return candidate;
    }
  }
  if (matrixIdParam && !Number.isNaN(parseInt(matrixIdParam, 10))) {
    return parseInt(matrixIdParam, 10);
  }
  return null;
};


const MatrixForm: React.FC = () => {
  const [isEditName, setIsEditName] = useState(false);
  const [severityLevels, setSeverityLevels] = useState<MatrixSeverityType[]>([]);
  const [likelihoodLevels, setLikelihoodLevels] = useState<MatrixLikelihoodType[]>([]);
  const [riskLevels, setRiskLevels] = useState<MatrixRiskType[]>([]);
  const [currentProject, setCurrentProject] = useState<ProjectType>();
  const [matrixListRefreshKey, setMatrixListRefreshKey] = useState(0);
  const [currentMatrix, setCurrentMatrix] = useState<MatrixType>();
  const [severityModalVisible, setSeverityModalVisible] = useState(false);
  const [likelihoodModalVisible, setLikelihoodModalVisible] = useState(false);
  const [cellModalVisible, setCellModalVisible] = useState(false);
  const [editSeverityModalVisible, setEditSeverityModalVisible] = useState(false);
  const [editLikelihoodModalVisible, setEditLikelihoodModalVisible] = useState(false);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<MatrixSeverityType | null>(null);
  const [selectedLikelihood, setSelectedLikelihood] = useState<MatrixLikelihoodType | null>(null);
  const [cellColor, setCellColor] = useState(DEFAULT_CELL_COLOR);
  const [fontColor, setFontColor] = useState(DEFAULT_FONT_COLOR);
  const [colorHistory, setColorHistory] = useState<string[]>(() => getInitialColorHistory());
  const [isMatrixInverse, setIsMatrixInverse] = useState(false);
  const [inverseLoading, setInverseLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [newMatrixName] = Form.useForm();
  const [severityForm] = Form.useForm();
  const [likelihoodForm] = Form.useForm();
  const [cellForm] = Form.useForm();
  const [editSeverityForm] = Form.useForm();
  const [editLikelihoodForm] = Form.useForm();
  const { project_id, matrix_id } = useParams();
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('matrixColorHistory', JSON.stringify(colorHistory));
  }, [colorHistory]);

  const registerColorUsage = useCallback((colorValue: string) => {
    const normalized = normalizeHexColor(colorValue);
    if (!normalized) return;
    setColorHistory((prev) => {
      const filtered = prev.filter((color) => color !== normalized);
      return [normalized, ...filtered].slice(0, MAX_COLOR_HISTORY);
    });
  }, []);

  const syncHistoryWithMatrix = useCallback((risks: MatrixRiskType[]) => {
    if (!risks?.length) return;
    setColorHistory((prev) => {
      const next = [...prev];
      risks.forEach((risk) => {
        const normalized = normalizeHexColor(risk?.cell_color);
        if (normalized && !next.includes(normalized)) {
          next.push(normalized);
        }
      });
      return next.slice(0, MAX_COLOR_HISTORY);
    });
  }, []);

  const handleHistoryColorClick = useCallback(
    (color: string) => {
      const normalized = normalizeHexColor(color);
      if (!normalized) return;
      setCellColor(normalized);
      registerColorUsage(normalized);
    },
    [registerColorUsage]
  );

  const renderPanelWithHistory = useCallback(
    (panel: ReactNode) => {
      if (!colorHistory.length) {
        return panel;
      }

      return (
        <div>
          <div style={{ padding: '8px 12px 8px', marginBottom: 8 }}>
            <p style={{ marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Colores usados recientemente</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
              {colorHistory.map((color) => {
                const fullHex = `#${color}`;
                const isActive = color === cellColor;
                return (
                  <Tooltip title={fullHex} key={color}>
                    <button
                      type='button'
                      aria-label={`Seleccionar color ${fullHex}`}
                      onClick={() => handleHistoryColorClick(color)}
                      style={{
                        width: 17,
                        height: 17,
                        borderRadius: 4,
                        border: isActive ? '2px solid #1677ff' : '1px solid #d9d9d9',
                        padding: 0,
                        backgroundColor: fullHex,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          </div>
          {panel}
        </div>
      );
    },
    [cellColor, colorHistory, handleHistoryColorClick]
  );

  const getCurrentMatrix = async () => {
    if (
      !project_id ||
      !matrix_id ||
      isNaN(parseInt(project_id as string, 10)) ||
      isNaN(parseInt(matrix_id as string, 10))
    ) {
      return;
    }
    const projectId = parseInt(project_id as string, 10);
    const matrixId = parseInt(matrix_id as string, 10);

    dispatch(
      getMatrix({
        project_id: projectId,
        matrix_id: matrixId,
      }) as any
    )
      .then((result: any) => {
        if (getMatrix.fulfilled.match(result)) {
          setCurrentMatrix(result.payload);
          const normalizedMatrix = result.payload as MatrixType;
          const normalizedSeverity =
            result.payload.matrix_severity?.map((level: MatrixSeverityType) => {
              if (level?.matrix?.id) {
                return level;
              }
              return {
                ...level,
                matrix: { ...(level.matrix ?? normalizedMatrix), id: normalizedMatrix.id } as MatrixType,
              };
            }) ?? [];
          const normalizedLikelihood =
            result.payload.matrix_likelihood?.map((level: MatrixLikelihoodType) => {
              if (level?.matrix?.id) {
                return level;
              }
              return {
                ...level,
                matrix: { ...(level.matrix ?? normalizedMatrix), id: normalizedMatrix.id } as MatrixType,
              };
            }) ?? [];
          setSeverityLevels(normalizedSeverity);
          setLikelihoodLevels(normalizedLikelihood);
          setRiskLevels(result.payload.matrix_risk ?? []);
          setIsMatrixInverse(Boolean(result.payload.inverse));
          syncHistoryWithMatrix(result.payload.matrix_risk ?? []);
          setIsEditName(false);
          newMatrixName.setFieldsValue({ name: result.payload.name });
        } else {
          setCurrentMatrix(undefined);
          setSeverityLevels([]);
          setLikelihoodLevels([]);
          setRiskLevels([]);
          triggerNotification('error', 'Error', 'No se pudo cargar la matriz', 'bottomRight');
        }
      })
      .catch(() => {
        setCurrentMatrix(undefined);
        setSeverityLevels([]);
        setLikelihoodLevels([]);
        setRiskLevels([]);
        triggerNotification('error', 'Error', 'No se pudo cargar la matriz', 'bottomRight');
      });
  };

  const getCurrentProject = async () => {
    if (!project_id || isNaN(parseInt(project_id as string, 10))) {
      triggerNotification('error', 'Error', 'Proyecto no válido', 'bottomRight');
      return;
    }

    dispatch(getProject(parseInt(project_id as string, 10)) as any)
      .then((result: any) => {
        if (getProject.fulfilled.match(result)) {
          setCurrentProject(result.payload);
          if (!matrix_id) {
            setIsEditName(true);
            newMatrixName.resetFields();
          }
        } else {
          triggerNotification('error', 'Error', 'No se pudo cargar el proyecto', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo cargar el proyecto', 'bottomRight');
      });
  };

  useEffect(() => {
    if (project_id) {
      getCurrentProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project_id]);

  useEffect(() => {
    if (matrix_id) {
      setCurrentMatrix(undefined);
      setSeverityLevels([]);
      setLikelihoodLevels([]);
      setRiskLevels([]);
      getCurrentMatrix();
    } else {
      setCurrentMatrix(undefined);
      setSeverityLevels([]);
      setLikelihoodLevels([]);
      setRiskLevels([]);
      setIsMatrixInverse(false);
      setIsEditName(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix_id]);

  useEffect(() => {
    if (severityModalVisible) {
      severityForm.resetFields();
    }
  }, [severityModalVisible, severityForm]);

  useEffect(() => {
    if (likelihoodModalVisible) {
      likelihoodForm.resetFields();
    }
  }, [likelihoodModalVisible, likelihoodForm]);

  useEffect(() => {
    if (editSeverityModalVisible && selectedSeverity) {
      editSeverityForm.setFieldsValue({ name: selectedSeverity.name });
    }
  }, [editSeverityModalVisible, selectedSeverity, editSeverityForm]);

  useEffect(() => {
    if (editLikelihoodModalVisible && selectedLikelihood) {
      editLikelihoodForm.setFieldsValue({ name: selectedLikelihood.name });
    }
  }, [editLikelihoodModalVisible, selectedLikelihood, editLikelihoodForm]);

  useEffect(() => {
    if (cellModalVisible && selectedCell) {
      cellForm.setFieldsValue({
        name: selectedCell.risk?.name ?? '',
        description: selectedCell.risk?.description ?? '',
      });
    }
  }, [cellModalVisible, selectedCell, cellForm]);

  const handleInverseChange = (checked: boolean) => {
    if (!currentProject?.id) {
      triggerNotification('error', 'Error', 'Selecciona un proyecto válido', 'bottomRight');
      return;
    }

    if (!currentMatrix?.id) {
      setIsMatrixInverse(checked);
      return;
    }

    const previousValue = isMatrixInverse;
    setIsMatrixInverse(checked);
    setInverseLoading(true);
    dispatch(
      updateMatrixInverse({
        projectId: currentProject.id as number,
        matrixId: currentMatrix.id as number,
        inverse: checked,
      }) as any
    )
      .then((result: any) => {
        if (updateMatrixInverse.fulfilled.match(result)) {
          const updatedInverse = Boolean(result.payload?.inverse ?? checked);
          setIsMatrixInverse(updatedInverse);
          setCurrentMatrix((prev) => (prev ? { ...prev, inverse: updatedInverse } : prev));
          triggerNotification(
            'success',
            'Éxito',
            updatedInverse
              ? 'La matriz se muestra con Severidad en filas y Probabilidad en columnas'
              : 'La matriz volvió a Severidad en columnas y Probabilidad en filas',
            'bottomRight'
          );
        } else {
          setIsMatrixInverse(previousValue);
          triggerNotification('error', 'Error', 'No se pudo actualizar la orientación de la matriz', 'bottomRight');
        }
      })
      .catch(() => {
        setIsMatrixInverse(previousValue);
        triggerNotification('error', 'Error', 'No se pudo actualizar la orientación de la matriz', 'bottomRight');
      })
      .finally(() => {
        setInverseLoading(false);
      });
  };

  const handleMatrixNameSubmit = (values: { name: string }) => {
    if (!currentProject) {
      triggerNotification('error', 'Error', 'Selecciona un proyecto válido', 'bottomRight');
      return;
    }
    const trimmedName = values.name?.trim();
    if (!trimmedName) {
      triggerNotification('error', 'Error', 'Nombre de la matriz es obligatorio', 'bottomRight');
      return;
    }

    if (!currentMatrix) {
      dispatch(createMatrix({ project: currentProject, name: trimmedName, inverse: isMatrixInverse } as MatrixType) as any)
        .then((result: any) => {
          if (createMatrix.fulfilled.match(result)) {
            window.location.href = `/proyecto/${currentProject.id}/matriz/${result.payload.id}/ver/`;
          } else {
            triggerNotification('error', 'Error', 'No se pudo crear la matriz', 'bottomRight');
          }
        })
        .catch(() => {
          triggerNotification('error', 'Error', 'No se pudo crear la matriz', 'bottomRight');
        });
      return;
    }

    dispatch(
      updateMatrix({
        project: currentProject,
        name: trimmedName,
        id: currentMatrix.id,
        inverse: isMatrixInverse,
      } as MatrixType) as any
    )
      .then((result: any) => {
        if (updateMatrix.fulfilled.match(result)) {
          setCurrentMatrix(result.payload);
          newMatrixName.setFieldsValue({ name: result.payload.name });
          setIsMatrixInverse(Boolean(result.payload.inverse));
          setIsEditName(false);
          setMatrixListRefreshKey((prev) => prev + 1);
          triggerNotification('success', 'Éxito', 'Matriz actualizada', 'bottomRight');
        } else {
          triggerNotification('error', 'Error', 'No se pudo actualizar la matriz', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo actualizar la matriz', 'bottomRight');
      });
  };

  const openSeverityModal = () => {
    if (!currentMatrix) return;
    setSeverityModalVisible(true);
  };

  const openLikelihoodModal = () => {
    if (!currentMatrix) return;
    setLikelihoodModalVisible(true);
  };

  const handleSeveritySubmit = async () => {
    if (!currentMatrix) return;
    let values;
    try {
      values = await severityForm.validateFields();
    } catch {
      return;
    }
    const resolvedMatrixId = resolveMatrixIdFromContext(matrix_id, currentMatrix?.id);
    if (!resolvedMatrixId) {
      triggerNotification('error', 'Error', 'No se pudo resolver el ID de la matriz', 'bottomRight');
      return;
    }
    const payload: MatrixSeverityType = {
      id: null,
      matrix: { ...(currentMatrix ?? {}), id: resolvedMatrixId } as MatrixType,
      name: values.name,
      description: values.description ?? '',
      lower_bound: parseOptionalNumber(values.lower_bound),
      upper_bound: parseOptionalNumber(values.upper_bound),
    };

    dispatch(createSeverityLevel(payload) as any)
      .then((result: any) => {
        if (createSeverityLevel.fulfilled.match(result)) {
          triggerNotification('success', 'Éxito', 'Columna agregada', 'bottomRight');
          setSeverityModalVisible(false);
          severityForm.resetFields();
          getCurrentMatrix();
        } else {
          triggerNotification('error', 'Error', 'No se pudo agregar la columna', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo agregar la columna', 'bottomRight');
      });
  };

  const handleLikelihoodSubmit = async () => {
    if (!currentMatrix) return;
    let values;
    try {
      values = await likelihoodForm.validateFields();
    } catch {
      return;
    }
    const resolvedMatrixId = resolveMatrixIdFromContext(matrix_id, currentMatrix?.id);
    if (!resolvedMatrixId) {
      triggerNotification('error', 'Error', 'No se pudo resolver el ID de la matriz', 'bottomRight');
      return;
    }
    const payload: MatrixLikelihoodType = {
      id: null,
      matrix: { ...(currentMatrix ?? {}), id: resolvedMatrixId } as MatrixType,
      name: values.name,
      description: values.description ?? '',
      lower_bound: parseOptionalNumber(values.lower_bound),
      upper_bound: parseOptionalNumber(values.upper_bound),
    };

    dispatch(createLikelihoodLevel(payload) as any)
      .then((result: any) => {
        if (createLikelihoodLevel.fulfilled.match(result)) {
          triggerNotification('success', 'Éxito', 'Fila agregada', 'bottomRight');
          setLikelihoodModalVisible(false);
          likelihoodForm.resetFields();
          getCurrentMatrix();
        } else {
          triggerNotification('error', 'Error', 'No se pudo agregar la fila', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo agregar la fila', 'bottomRight');
      });
  };

  const openEditSeverityModal = (severity: MatrixSeverityType) => {
    if (!currentMatrix) return;
    if (!severity.id) {
      triggerNotification('warning', 'Atención', 'Debes guardar la columna antes de editarla', 'bottomRight');
      return;
    }
    setSelectedSeverity(severity);
    setEditSeverityModalVisible(true);
  };

  const openEditLikelihoodModal = (likelihood: MatrixLikelihoodType) => {
    if (!currentMatrix) return;
    if (!likelihood.id) {
      triggerNotification('warning', 'Atención', 'Debes guardar la fila antes de editarla', 'bottomRight');
      return;
    }
    setSelectedLikelihood(likelihood);
    setEditLikelihoodModalVisible(true);
  };

  const closeEditSeverityModal = () => {
    setEditSeverityModalVisible(false);
    setSelectedSeverity(null);
    editSeverityForm.resetFields();
  };

  const closeEditLikelihoodModal = () => {
    setEditLikelihoodModalVisible(false);
    setSelectedLikelihood(null);
    editLikelihoodForm.resetFields();
  };

  const handleDeleteSeverity = async (severity: MatrixSeverityType) => {
    const matrixId =
      severity?.matrix?.id ??
      currentMatrix?.id ??
      (matrix_id && !isNaN(parseInt(matrix_id, 10)) ? parseInt(matrix_id, 10) : null);

    if (!severity?.id || !matrixId) {
      triggerNotification('warning', 'Atención', 'Debes guardar la columna y la matriz antes de eliminar', 'bottomRight');
      return;
    }

    const payload: MatrixSeverityType = {
      ...severity,
      matrix: { ...(severity.matrix ?? currentMatrix), id: matrixId } as MatrixType,
    };

    return dispatch(deleteSeverityLevel(payload) as any)
      .then((result: any) => {
        if (deleteSeverityLevel.fulfilled.match(result)) {
          triggerNotification('success', 'Columna eliminada', 'La columna se eliminó correctamente', 'bottomRight');
          setSeverityLevels((prev) => prev.filter((item) => item.id !== severity.id));
          getCurrentMatrix();
        } else {
          triggerNotification('error', 'Error', 'No se pudo eliminar la columna', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo eliminar la columna', 'bottomRight');
      });
  };

  const handleDeleteLikelihood = async (likelihood: MatrixLikelihoodType) => {
    const matrixId =
      likelihood?.matrix?.id ??
      currentMatrix?.id ??
      (matrix_id && !isNaN(parseInt(matrix_id, 10)) ? parseInt(matrix_id, 10) : null);

    if (!likelihood?.id || !matrixId) {
      triggerNotification('warning', 'Atención', 'Debes guardar la fila y la matriz antes de eliminar', 'bottomRight');
      return;
    }

    const payload: MatrixLikelihoodType = {
      ...likelihood,
      matrix: { ...(likelihood.matrix ?? currentMatrix), id: matrixId } as MatrixType,
    };

    return dispatch(deleteLikelihoodLevel(payload) as any)
      .then((result: any) => {
        if (deleteLikelihoodLevel.fulfilled.match(result)) {
          triggerNotification('success', 'Fila eliminada', 'La fila se eliminó correctamente', 'bottomRight');
          setLikelihoodLevels((prev) => prev.filter((item) => item.id !== likelihood.id));
          getCurrentMatrix();
        } else {
          triggerNotification('error', 'Error', 'No se pudo eliminar la fila', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo eliminar la fila', 'bottomRight');
      });
  };

  const confirmDeleteSeverity = (severity: MatrixSeverityType) => {
    Modal.confirm({
      title: 'Eliminar columna',
      content: `¿Deseas eliminar la columna "${severity.name}"? También se eliminarán sus celdas asociadas.`,
      okText: 'Eliminar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      centered: true,
      onOk: () => handleDeleteSeverity(severity),
    });
  };

  const confirmDeleteLikelihood = (likelihood: MatrixLikelihoodType) => {
    Modal.confirm({
      title: 'Eliminar fila',
      content: `¿Deseas eliminar la fila "${likelihood.name}"? También se eliminarán sus celdas asociadas.`,
      okText: 'Eliminar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      centered: true,
      onOk: () => handleDeleteLikelihood(likelihood),
    });
  };

  const handleEditSeveritySubmit = async () => {
    if (!selectedSeverity || !currentMatrix) return;
    let values;
    try {
      values = await editSeverityForm.validateFields();
    } catch {
      return;
    }
    const trimmedName = values.name?.trim();
    if (!trimmedName) {
      triggerNotification('error', 'Error', 'Ingresa un nombre válido', 'bottomRight');
      return;
    }
    const resolvedMatrixId = resolveMatrixIdFromContext(
      matrix_id,
      selectedSeverity?.matrix?.id,
      currentMatrix?.id
    );
    if (!resolvedMatrixId) {
      triggerNotification('error', 'Error', 'No se pudo resolver el ID de la matriz', 'bottomRight');
      return;
    }
    const payload: MatrixSeverityType = {
      ...selectedSeverity,
      matrix: { ...(selectedSeverity.matrix ?? currentMatrix), id: resolvedMatrixId } as MatrixType,
      name: trimmedName,
    };

    dispatch(updateSeverityLevel(payload) as any)
      .then((result: any) => {
        if (updateSeverityLevel.fulfilled.match(result)) {
          triggerNotification('success', 'Éxito', 'Nombre de columna actualizado', 'bottomRight');
          closeEditSeverityModal();
          getCurrentMatrix();
        } else {
          triggerNotification('error', 'Error', 'No se pudo actualizar la columna', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo actualizar la columna', 'bottomRight');
      });
  };

  const handleEditLikelihoodSubmit = async () => {
    if (!selectedLikelihood || !currentMatrix) return;
    let values;
    try {
      values = await editLikelihoodForm.validateFields();
    } catch {
      return;
    }
    const trimmedName = values.name?.trim();
    if (!trimmedName) {
      triggerNotification('error', 'Error', 'Ingresa un nombre válido', 'bottomRight');
      return;
    }
    const resolvedMatrixId = resolveMatrixIdFromContext(
      matrix_id,
      selectedLikelihood?.matrix?.id,
      currentMatrix?.id
    );
    if (!resolvedMatrixId) {
      triggerNotification('error', 'Error', 'No se pudo resolver el ID de la matriz', 'bottomRight');
      return;
    }
    const payload: MatrixLikelihoodType = {
      ...selectedLikelihood,
      matrix: { ...(selectedLikelihood.matrix ?? currentMatrix), id: resolvedMatrixId } as MatrixType,
      name: trimmedName,
    };

    dispatch(updateLikelihoodLevel(payload) as any)
      .then((result: any) => {
        if (updateLikelihoodLevel.fulfilled.match(result)) {
          triggerNotification('success', 'Éxito', 'Nombre de fila actualizado', 'bottomRight');
          closeEditLikelihoodModal();
          getCurrentMatrix();
        } else {
          triggerNotification('error', 'Error', 'No se pudo actualizar la fila', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo actualizar la fila', 'bottomRight');
      });
  };

  const handleCellSelection = (cell: SelectedCell) => {
    if (!currentMatrix) return;
    setSelectedCell(cell);
    setCellColor(normalizeHexColor(cell.risk?.cell_color) || DEFAULT_CELL_COLOR);
    setFontColor(normalizeHexColor(cell.risk?.font_color) || DEFAULT_FONT_COLOR);
    setCellModalVisible(true);
  };

  const closeCellModal = () => {
    setCellModalVisible(false);
    setSelectedCell(null);
    cellForm.resetFields();
  };

  const handleExportMatrix = async () => {
    if (!currentMatrix) {
      triggerNotification('warning', 'Atención', 'Guarda la matriz antes de exportarla', 'bottomRight');
      return;
    }

    if (!severityLevels.length || !likelihoodLevels.length) {
      triggerNotification('warning', 'Atención', 'Agrega filas y columnas antes de exportar', 'bottomRight');
      return;
    }

    try {
      setExporting(true);
      await exportRiskMatrixToXlsx({
        matrixName: currentMatrix.name,
        severityLevels,
        likelihoodLevels,
        riskLevels,
        inverse: isMatrixInverse,
      });
      triggerNotification('success', 'Éxito', 'Matriz exportada en Excel', 'bottomRight');
    } catch (error) {
      console.error('Matrix export error', error);
      triggerNotification('error', 'Error', 'No se pudo exportar la matriz', 'bottomRight');
    } finally {
      setExporting(false);
    }
  };

  const handleCellSubmit = async () => {
    if (!selectedCell || !currentMatrix) return;
    let values;
    try {
      values = await cellForm.validateFields();
    } catch {
      return;
    }

    const resolvedMatrixId = resolveMatrixIdFromContext(
      matrix_id,
      currentMatrix?.id,
      selectedCell.severity?.matrix?.id,
      selectedCell.likelihood?.matrix?.id
    );
    if (!resolvedMatrixId) {
      triggerNotification('error', 'Error', 'No se pudo resolver el ID de la matriz', 'bottomRight');
      return;
    }
    const payload: MatrixRiskType = {
      id: selectedCell.risk?.id ?? null,
      matrix: { ...(currentMatrix ?? {}), id: resolvedMatrixId } as MatrixType,
      name: values.name,
      description: values.description ?? '',
      severity: selectedCell.severity,
      likelihood: selectedCell.likelihood,
      cell_color: normalizeHexColor(cellColor) || DEFAULT_CELL_COLOR,
      font_color: normalizeHexColor(fontColor) || DEFAULT_FONT_COLOR,
    };

    const action = selectedCell.risk ? updateRiskLevel : createRiskLevel;

    dispatch(action(payload) as any)
      .then((result: any) => {
        if (action.fulfilled.match(result)) {
          triggerNotification('success', 'Éxito', 'Celda actualizada', 'bottomRight');
          registerColorUsage(cellColor);
          closeCellModal();
          getCurrentMatrix();
        } else {
          triggerNotification('error', 'Error', 'No se pudo actualizar la celda', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo actualizar la celda', 'bottomRight');
      });
  };

  return (
    <>
      <Row className='content'>
        <Col sm={5}>
          <div className='map-container'>
            {project_id ? (
              <MatrixList
                project_id={project_id}
                activeMatrixId={currentMatrix?.id ?? null}
                refreshKey={matrixListRefreshKey}
              />
            ) : (
              <p>Selecciona un proyecto para ver sus matrices.</p>
            )}
          </div>
        </Col>
        <Col sm={1}></Col>
        <Col sm={18}>
          <div className='title-full'>
            <h1>
              {currentProject ? (
                <Link to={`/proyecto/${currentProject.id}/ver`}>{currentProject.name}</Link>
              ) : (
                'Proyecto'
              )}{' '}
              <RightOutlined /> {currentMatrix?.name ?? 'Nueva matriz'}{' '}
              {currentMatrix && !isEditName ? (
                <Button type='link' onClick={() => setIsEditName(true)}>
                  Editar nombre
                </Button>
              ) : null}
            </h1>
          </div>
          <div className='ant-divider'></div>

          {(isEditName || !currentMatrix) && (
            <Form
              form={newMatrixName}
              layout='inline'
              onFinish={handleMatrixNameSubmit}
              style={{ marginBottom: '1rem' }}
            >
              <Form.Item
                name='name'
                label='Nombre de la matriz'
                rules={[{ required: true, message: 'Ingresa el nombre de la matriz' }]}
              >
                <Input placeholder='Nombre de la matriz' style={{ minWidth: '240px' }} />
              </Form.Item>
              <Form.Item>
                <Button type='primary' htmlType='submit'>
                  Guardar
                </Button>
                {currentMatrix && (
                  <Button
                    htmlType='button'
                    style={{ marginLeft: '.5rem' }}
                    onClick={() => {
                      newMatrixName.setFieldsValue({ name: currentMatrix.name });
                      setIsEditName(false);
                    }}
                  >
                    Cancelar
                  </Button>
                )}
              </Form.Item>
            </Form>
          )}

          {currentProject && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 1rem',
                background: '#f8f9fb',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                marginBottom: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Switch
                  checked={isMatrixInverse}
                  loading={inverseLoading}
                  onChange={handleInverseChange}
                  disabled={!currentProject}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Invertir matriz</div>
                  <div style={{ color: '#4b5563', fontSize: 12 }}>
                    {isMatrixInverse
                      ? 'Severidad en filas y Probabilidad en columnas'
                      : 'Severidad en columnas y Probabilidad en filas'}
                  </div>
                </div>
              </div>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportMatrix}
                type='primary'
                ghost
                loading={exporting}
                disabled={!currentMatrix}
              >
                Exportar a Excel
              </Button>
            </div>
          )}

          <div className='map-container'>
            {currentMatrix ? (
              <RiskMatrix
                severityLevels={severityLevels}
                likelihoodLevels={likelihoodLevels}
                riskLevels={riskLevels}
                inverse={isMatrixInverse}
                onSelectCell={handleCellSelection}
                onAddSeverity={openSeverityModal}
                onAddLikelihood={openLikelihoodModal}
                onEditSeverity={openEditSeverityModal}
                onEditLikelihood={openEditLikelihoodModal}
                onDeleteSeverity={confirmDeleteSeverity}
                onDeleteLikelihood={confirmDeleteLikelihood}
              />
            ) : (
              <p>Ingresa el nombre de la nueva matriz para comenzar a configurarla.</p>
            )}
          </div>
        </Col>
      </Row>

      <Modal
        title='Agregar nueva columna (Severidad)'
        open={severityModalVisible}
        onCancel={() => setSeverityModalVisible(false)}
        onOk={handleSeveritySubmit}
        okText='Guardar'
        destroyOnClose
      >
        <Form form={severityForm} layout='vertical'>
          <Form.Item name='name' label='Nombre' rules={[{ required: true, message: 'Ingresa un nombre' }]}>
            <Input placeholder='Nombre' />
          </Form.Item>
          <Form.Item name='description' label='Descripción'>
            <Input placeholder='Descripción' />
          </Form.Item>
          <Collapse ghost>
            <Collapse.Panel header='Otras opciones' key='severityOptions'>
              <Form.Item name='lower_bound' label='Límite inferior'>
                <Input type='number' placeholder='0' />
              </Form.Item>
              <Form.Item name='upper_bound' label='Límite superior'>
                <Input type='number' placeholder='100' />
              </Form.Item>
            </Collapse.Panel>
          </Collapse>
        </Form>
      </Modal>

      <Modal
        title='Agregar nueva fila (Probabilidad)'
        open={likelihoodModalVisible}
        onCancel={() => setLikelihoodModalVisible(false)}
        onOk={handleLikelihoodSubmit}
        okText='Guardar'
        destroyOnClose
      >
        <Form form={likelihoodForm} layout='vertical'>
          <Form.Item name='name' label='Nombre' rules={[{ required: true, message: 'Ingresa un nombre' }]}>
            <Input placeholder='Nombre' />
          </Form.Item>
          <Form.Item name='description' label='Descripción'>
            <Input placeholder='Descripción' />
          </Form.Item>
          <Collapse ghost>
            <Collapse.Panel header='Otras opciones' key='likelihoodOptions'>
              <Form.Item name='lower_bound' label='Límite inferior'>
                <Input type='number' placeholder='0' />
              </Form.Item>
              <Form.Item name='upper_bound' label='Límite superior'>
                <Input type='number' placeholder='100' />
              </Form.Item>
            </Collapse.Panel>
          </Collapse>
        </Form>
      </Modal>

      <Modal
        title={selectedSeverity ? `Editar columna (${selectedSeverity.name})` : 'Editar columna'}
        open={editSeverityModalVisible}
        onCancel={closeEditSeverityModal}
        onOk={handleEditSeveritySubmit}
        okText='Guardar'
        destroyOnClose
      >
        <Form form={editSeverityForm} layout='vertical'>
          <Form.Item name='name' label='Nombre' rules={[{ required: true, message: 'Ingresa un nombre' }]}>
            <Input placeholder='Nombre de la columna' />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={selectedLikelihood ? `Editar fila (${selectedLikelihood.name})` : 'Editar fila'}
        open={editLikelihoodModalVisible}
        onCancel={closeEditLikelihoodModal}
        onOk={handleEditLikelihoodSubmit}
        okText='Guardar'
        destroyOnClose
      >
        <Form form={editLikelihoodForm} layout='vertical'>
          <Form.Item name='name' label='Nombre' rules={[{ required: true, message: 'Ingresa un nombre' }]}>
            <Input placeholder='Nombre de la fila' />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          selectedCell
            ? `Editar celda (${selectedCell.severity.name} / ${selectedCell.likelihood.name})`
            : 'Editar celda'
        }
        open={cellModalVisible}
        onCancel={closeCellModal}
        onOk={handleCellSubmit}
        okText='Guardar'
        destroyOnClose
      >
        <Form form={cellForm} layout='vertical'>
          <Form.Item
            name='name'
            label='Nombre Corto (3 caracteres)'
            rules={[{ required: true, message: 'Ingresa un nombre corto' }]}
          >
            <Input placeholder='Nombre corto del riesgo' maxLength={3} />
          </Form.Item>
          <Form.Item
            name='description'
            label='Nombre Completo'
            rules={[{ required: true, message: 'El nombre completo es obligatorio' }]}
          >
            <Input.TextArea rows={3} placeholder='Nombre completo del riesgo' />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <label>Color del texto</label>
              <br />
              <ColorPicker
                value={`#${fontColor}`}
                showText
                format='hex'
                onChange={(value, css) => {
                  const normalized = extractHexFromPicker(value, css) || DEFAULT_FONT_COLOR;
                  setFontColor(normalized);
                }}
              />
            </Col>
            <Col span={12}>
              <label>Color de la celda</label>
              <br />
              <ColorPicker
                value={`#${cellColor}`}
                showText
                format='hex'
                panelRender={(panel) => renderPanelWithHistory(panel)}
                onChange={(value, css) => {
                  const normalized = extractHexFromPicker(value, css) || DEFAULT_CELL_COLOR;
                  setCellColor(normalized);
                }}
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default MatrixForm;
